import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@toki/auth';
import type { Area, InternalUser, RoleWithPermissions } from '../services/api';
import {
  createInternalUser,
  deactivateUser,
  listAreas,
  listInternalUsers,
  listRoles,
  updateInternalUser,
  UserHasOpenTicketsError,
} from '../services/api';
import { ROLE_DESCRIPTIONS, ROLE_LABELS } from '../config/roles';
import { usePermissions } from '../hooks/usePermissions';
import styles from './Mantenimiento.module.css';

/**
 * TÉCNICOS: el staff (paso 85a). Era la pestaña «Equipo» de Usuarios.
 *
 * Al staff no se le borra nunca: se le desactiva (K41). Y desde el 85a, si
 * lleva tickets abiertos hay que decir qué pasa con ellos —pasárselos a otro o
 * dejarlos sin asignar— o se quedarían asignados a alguien que no recibe los
 * avisos ni puede entrar, y nadie se enteraría. Lo exige el backend
 * (409 `USER_HAS_OPEN_TICKETS`); esta pantalla lo pregunta antes.
 */
export function TecnicosScreen() {
  const { provider } = useAuth();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('users:manage');
  const [users, setUsers] = useState<InternalUser[]>([]);
  const [roles, setRoles] = useState<RoleWithPermissions[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<InternalUser | null>(null);
  const [saving, setSaving] = useState(false);
  const [showRoleHelp, setShowRoleHelp] = useState(false);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [roleId, setRoleId] = useState('');
  const [areaIds, setAreaIds] = useState<string[]>([]);

  // Desactivar con tickets abiertos.
  const [leaving, setLeaving] = useState<{ user: InternalUser; open: number } | null>(null);
  const [mode, setMode] = useState<'reassign' | 'unassign'>('reassign');
  const [heir, setHeir] = useState('');

  const load = useCallback(async () => {
    const token = await provider.getAccessToken();
    if (!token) return;
    try {
      const [u, r, a] = await Promise.all([listInternalUsers(token), listRoles(token), listAreas(token)]);
      setUsers(u);
      setRoles(r);
      setAreas(a.filter((x) => x.isActive));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [provider]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setEmail('');
    setDisplayName('');
    setPassword('');
    setRoleId(roles.find((r) => r.name === 'agent')?.id ?? '');
    setAreaIds([]);
    setError(null);
    setShowRoleHelp(false);
    setModalOpen(true);
  };

  const openEdit = (user: InternalUser) => {
    setEditing(user);
    setEmail(user.email);
    setDisplayName(user.displayName ?? '');
    setPassword('');
    setRoleId(user.roleId);
    setAreaIds(user.areas.map((a) => a.id));
    setError(null);
    setShowRoleHelp(false);
    setModalOpen(true);
  };

  const toggleArea = (id: string) =>
    setAreaIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSave = async () => {
    const token = await provider.getAccessToken();
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        const updates: Record<string, unknown> = { displayName, roleId, areaIds };
        if (email !== editing.email) updates.email = email;
        if (password) updates.password = password;
        await updateInternalUser(editing.id, updates as any, token);
      } else {
        await createInternalUser({ email, displayName, password, roleId, areaIds }, token);
      }
      setModalOpen(false);
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const reactivate = async (u: InternalUser) => {
    const token = await provider.getAccessToken();
    if (!token) return;
    setError(null);
    try {
      await updateInternalUser(u.id, { isActive: true }, token);
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const startDeactivate = async (u: InternalUser) => {
    setError(null);
    if (u.openTickets > 0) {
      setMode('reassign');
      setHeir('');
      setLeaving({ user: u, open: u.openTickets });
      return;
    }
    const token = await provider.getAccessToken();
    if (!token) return;
    try {
      await deactivateUser(u.id, token);
      await load();
    } catch (e: any) {
      // Le han asignado algo entre que se pintó la lista y el clic.
      if (e instanceof UserHasOpenTicketsError) {
        setMode('reassign');
        setHeir('');
        setLeaving({ user: u, open: e.openTickets });
      } else setError(e.message);
    }
  };

  const confirmDeactivate = async () => {
    if (!leaving) return;
    const token = await provider.getAccessToken();
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      await deactivateUser(
        leaving.user.id,
        token,
        mode === 'reassign' ? { mode, reassignTo: heir } : { mode: 'unassign' },
      );
      setLeaving(null);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // A quién se le pueden pasar: staff activo que no es el que se va.
  const herederos = leaving ? users.filter((u) => u.isActive && u.id !== leaving.user.id) : [];

  if (loading) return <div className={styles.loading}>Cargando técnicos…</div>;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Técnicos</h2>
        {canManage && <button className={styles.createBtn} onClick={openCreate}>+ Nuevo técnico</button>}
      </div>

      {error && !modalOpen && !leaving && <div className={styles.error} style={{ margin: '0 0 12px' }}>{error}</div>}

      <div className={styles.tableCard}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Email</th>
              <th>Rol</th>
              <th>Equipos</th>
              <th>Abiertos</th>
              <th>Estado</th>
              {canManage && <th>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={!u.isActive ? styles.inactive : ''}>
                <td>{u.displayName || '—'}</td>
                <td>{u.email}</td>
                <td><span className={styles.roleBadge}>{ROLE_LABELS[u.roleName] ?? u.roleName}</span></td>
                <td>{u.areas.map((a) => a.name).join(', ') || '—'}</td>
                <td className={styles.num}>{u.openTickets}</td>
                <td>
                  <span className={u.isActive ? styles.badgeActive : styles.badgeInactive}>
                    {u.isActive ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                {canManage && (
                  <td>
                    <div className={styles.actions}>
                    <button className={styles.editBtn} onClick={() => openEdit(u)}>Editar</button>
                    {u.isActive ? (
                      <button className={styles.deactivateBtn} onClick={() => startDeactivate(u)}>Desactivar</button>
                    ) : (
                      <button className={styles.editBtn} onClick={() => reactivate(u)}>Reactivar</button>
                    )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={canManage ? 7 : 6} className={styles.empty}>No hay técnicos</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Desactivar con tickets abiertos (85a) ───────────── */}
      {leaving && (
        <div className={styles.backdrop} onClick={() => setLeaving(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Desactivar técnico">
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Desactivar a {leaving.user.displayName || leaving.user.email}</h3>
              <button type="button" className={styles.modalClose} onClick={() => setLeaving(null)} aria-label="Cerrar">&times;</button>
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <div className={styles.modalBody}>
              <div className={styles.notice}>
                Tiene <strong>{leaving.open} {leaving.open === 1 ? 'ticket abierto' : 'tickets abiertos'}</strong>.
                Desactivado no puede entrar ni recibe avisos: si se quedan a su nombre, nadie se entera.
              </div>
              <label className={styles.radioRow}>
                <input type="radio" name="leaving-mode" checked={mode === 'reassign'} onChange={() => setMode('reassign')} />
                Pasárselos a
              </label>
              <select
                className={styles.select}
                aria-label="Técnico que los hereda"
                value={heir}
                onChange={(e) => { setHeir(e.target.value); setMode('reassign'); }}
                disabled={mode !== 'reassign'}
              >
                <option value="">Elige un técnico…</option>
                {herederos.map((u) => (
                  <option key={u.id} value={u.id}>{u.displayName || u.email}</option>
                ))}
              </select>
              <label className={styles.radioRow}>
                <input type="radio" name="leaving-mode" checked={mode === 'unassign'} onChange={() => setMode('unassign')} />
                Dejarlos sin asignar (siguen en su equipo, en «Sin asignar»)
              </label>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setLeaving(null)}>Cancelar</button>
              <button
                className={styles.dangerBtn}
                onClick={confirmDeactivate}
                disabled={saving || (mode === 'reassign' && !heir)}
              >
                {saving ? 'Desactivando…' : 'Desactivar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Alta / edición ──────────────────────────────────── */}
      {modalOpen && (
        <div className={styles.backdrop} onClick={() => setModalOpen(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{editing ? 'Editar técnico' : 'Nuevo técnico'}</h3>
              <button type="button" className={styles.modalClose} onClick={() => setModalOpen(false)} aria-label="Cerrar">&times;</button>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.modalBody}>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Email</label>
                <input className={styles.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Nombre</label>
                <input className={styles.input} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>{editing ? 'Nueva contraseña (vacía para no cambiarla)' : 'Contraseña'}</label>
                <input className={styles.input} type="password" placeholder="Mín. 8 caracteres" value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              <div className={styles.fieldGroup}>
                <div className={styles.labelRow}>
                  <label className={styles.label}>Rol</label>
                  <button type="button" className={styles.helpBtn} onClick={() => setShowRoleHelp((o) => !o)} title="Ver descripción de los roles">?</button>
                </div>
                {showRoleHelp && (
                  <div className={styles.roleHelp}>
                    {Object.entries(ROLE_DESCRIPTIONS).map(([key, desc]) => (
                      <div key={key}><strong>{ROLE_LABELS[key]}</strong> &mdash; {desc}</div>
                    ))}
                  </div>
                )}
                <select className={styles.select} value={roleId} onChange={(e) => setRoleId(e.target.value)}>
                  <option value="">Seleccionar rol…</option>
                  {roles.filter((r) => r.name !== 'requester' && r.name !== 'superadmin').map((r) => (
                    <option key={r.id} value={r.id}>{ROLE_LABELS[r.name] ?? r.name}</option>
                  ))}
                </select>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Equipos</label>
                <div className={styles.pillGroup}>
                  {areas.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      className={`${styles.pill} ${areaIds.includes(a.id) ? styles.pillActive : ''}`}
                      onClick={() => toggleArea(a.id)}
                    >
                      {a.name}
                    </button>
                  ))}
                  {areas.length === 0 && <span className={styles.muted}>No hay equipos creados</span>}
                </div>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setModalOpen(false)}>Cancelar</button>
              <button
                className={styles.saveBtn}
                onClick={handleSave}
                disabled={saving || !displayName.trim() || !roleId || !email || (!editing && password.length < 8) || (!!password && password.length < 8)}
              >
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
